/**
 * CRUD pages for tracking admin: SLA Config, Guard Companies, Guard Posts, Incident Types.
 */
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Switch,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { createColumnHelper } from "@tanstack/react-table";
import { useCallback, useMemo, useRef, useState } from "react";
import WelcomeSection from "components/WelcomeSection";
import { ReactTable } from "components/ReactTable";
import { useTrackingAdminList } from "hooks/useTrackingAdminList";
import { useAuthentication } from "hooks/useAuthentication";
import {
  fetchSLAConfig,
  createSLAConfig,
  updateSLAConfig,
  deleteSLAConfig,
  type SLAConfigRow,
  type SLAConfigPayload,
} from "services/tracking/trackingAdminService";
import {
  fetchGuardCompanies,
  createGuardCompany,
  updateGuardCompany,
  deleteGuardCompany,
  type GuardCompanyRow,
  type GuardCompanyPayload,
} from "services/tracking/trackingAdminService";
import {
  fetchGuardPosts,
  createGuardPost,
  updateGuardPost,
  deleteGuardPost,
  type GuardPostRow,
  type GuardPostPayload,
} from "services/tracking/trackingAdminService";
import { fetchGuardSites } from "services/tracking/guardService";
import {
  fetchIncidentTypes,
  createIncidentType,
  updateIncidentType,
  deleteIncidentType,
  type IncidentTypeRow,
  type IncidentTypePayload,
} from "services/tracking/trackingAdminService";
import { showToast, toastTypes } from "utils";
import useSWR from "swr";

function useSites() {
  const { user } = useAuthentication();
  const token = user?.auth?.access_token;
  const { data } = useSWR(token ? ["guard-sites", token] : null, () => fetchGuardSites(token));
  return Array.isArray(data) ? data : [];
}

// --- SLA Config ---
export function TrackingSLAConfigPage() {
  const toast = useToast();
  const cancelRef = useRef<any>(null);
  const { user } = useAuthentication();
  const token = user?.auth?.access_token;
  const { data: list, isLoading, error, refetch } = useTrackingAdminList("sla-config", fetchSLAConfig);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isConfirmOpen, onOpen: onConfirmOpen, onClose: onConfirmClose } = useDisclosure();
  const [form, setForm] = useState<SLAConfigPayload>({ name: "", severity: null, incident_type_id: null, max_response_minutes: 0, max_resolution_minutes: null, is_active: true });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [activeItem, setActiveItem] = useState<SLAConfigRow | null>(null);

  const handleCreate = useCallback(() => { setEditingId(null); setForm({ name: "", severity: null, incident_type_id: null, max_response_minutes: 0, max_resolution_minutes: null, is_active: true }); onOpen(); }, [onOpen]);
  const handleEdit = useCallback((row: SLAConfigRow) => {
    setEditingId(row.id);
    setForm({ name: row.name, severity: row.severity ?? null, incident_type_id: row.incident_type_id ?? null, max_response_minutes: row.max_response_minutes, max_resolution_minutes: row.max_resolution_minutes ?? null, is_active: row.is_active });
    onOpen();
  }, [onOpen]);
  const handleSubmit = useCallback(async () => {
    if (!form.name.trim()) { showToast({ toast, message: "Nom obligatoire.", status: toastTypes.error }); return; }
    setSubmitting(true);
    try {
      if (editingId) { await updateSLAConfig(token, editingId, form); showToast({ toast, message: "Config mise à jour.", status: toastTypes.success }); }
      else { await createSLAConfig(token, form); showToast({ toast, message: "Config créée.", status: toastTypes.success }); }
      refetch(); onClose();
    } catch (e: any) { showToast({ toast, message: e?.response?.data?.detail || "Erreur.", status: toastTypes.error }); } finally { setSubmitting(false); }
  }, [token, form, editingId, refetch, onClose, toast]);
  const handleDeleteClick = useCallback((row: SLAConfigRow) => { setActiveItem(row); onConfirmOpen(); }, [onConfirmOpen]);
  const handleDeleteConfirm = useCallback(async () => {
    if (!activeItem) return;
    setDeletingId(activeItem.id);
    try {
      await deleteSLAConfig(token, activeItem.id);
      showToast({ toast, message: "Config supprimée.", status: toastTypes.success });
      refetch();
      onConfirmClose();
    } catch (e: any) { showToast({ toast, message: e?.response?.data?.detail || "Erreur.", status: toastTypes.error }); } finally { setDeletingId(null); setActiveItem(null); }
  }, [token, activeItem, refetch, onConfirmClose, toast]);

  const col = createColumnHelper<SLAConfigRow>();
  const columns = useMemo(() => [
    col.accessor("name", { header: "Nom", cell: (c) => <Text>{c.getValue()}</Text> }),
    col.accessor("max_response_minutes", { header: "Réponse (min)", cell: (c) => <Text>{c.getValue()}</Text> }),
    col.accessor("is_active", { header: "Actif", cell: (c) => <Text>{c.getValue() ? "Oui" : "Non"}</Text> }),
    col.display({ id: "action", header: "Action", cell: ({ row }) => <><Button size="sm" onClick={() => handleEdit(row.original)} mr={2}>Modifier</Button><Button size="sm" colorScheme="red" onClick={() => handleDeleteClick(row.original)}>Supprimer</Button></> }),
  ], [handleEdit, handleDeleteClick]);

  return (
    <>
      <Flex gap="18px" bg="very_light_gray" flex={1} flexDirection="column" py="20px">
        <WelcomeSection />
        <Flex flexDirection="column" px={{ base: "20px", sm: "24px" }}>
          <Flex justifyContent="space-between" alignItems="center" mb={4}>
            <Heading size="text2xl" as="h5" fontFamily="Arimo">Configurations SLA</Heading>
            <Button colorScheme="blue" onClick={handleCreate}>Ajouter</Button>
          </Flex>
          {error && <Box p={4} bg="red.50" borderRadius="md"><Text color="red.700">Erreur chargement.</Text></Box>}
          <Box bg="white" borderRadius="10px">
            {isLoading ? <Flex justify="center" py={10}><Spinner /></Flex> : <ReactTable columns={columns as any} data={list} size="sm" />}
            {!isLoading && !list.length && <Box p={8} textAlign="center"><Text color="gray.600">Aucune donnée.</Text></Box>}
          </Box>
        </Flex>
      </Flex>
      <Modal isOpen={isOpen} onClose={onClose}><ModalOverlay /><ModalContent>
        <ModalHeader>{editingId ? "Modifier" : "Ajouter"} une config SLA</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={3}>
            <FormControl><FormLabel>Nom</FormLabel><Input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} /></FormControl>
            <FormControl><FormLabel>Réponse max (min)</FormLabel><Input type="number" value={form.max_response_minutes} onChange={(e) => setForm((p) => ({ ...p, max_response_minutes: parseInt(e.target.value, 10) || 0 }))} /></FormControl>
            <FormControl><FormLabel>Résolution max (min)</FormLabel><Input type="number" value={form.max_resolution_minutes ?? ""} onChange={(e) => setForm((p) => ({ ...p, max_resolution_minutes: e.target.value ? parseInt(e.target.value, 10) : null }))} /></FormControl>
            <FormControl display="flex" alignItems="center"><FormLabel mb={0}>Actif</FormLabel><Switch isChecked={form.is_active} onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))} /></FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter><Button variant="ghost" mr={3} onClick={onClose}>Annuler</Button><Button colorScheme="blue" onClick={handleSubmit} isLoading={submitting}>Enregistrer</Button></ModalFooter>
      </ModalContent></Modal>
      <AlertDialog isOpen={isConfirmOpen} leastDestructiveRef={cancelRef} onClose={onConfirmClose}>
        <AlertDialogOverlay /><AlertDialogContent>
          <AlertDialogHeader>Supprimer cette config SLA ?</AlertDialogHeader>
          <AlertDialogBody>Cette action est irréversible.</AlertDialogBody>
          <AlertDialogFooter><Button ref={cancelRef} onClick={onConfirmClose}>Annuler</Button><Button colorScheme="red" onClick={handleDeleteConfirm} isLoading={!!deletingId} ml={3}>Supprimer</Button></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// --- Guard Companies ---
export function TrackingGuardCompaniesPage() {
  const toast = useToast();
  const cancelRef = useRef<any>(null);
  const { user } = useAuthentication();
  const token = user?.auth?.access_token;
  const { data: list, isLoading, error, refetch } = useTrackingAdminList("guard-companies", fetchGuardCompanies);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isConfirmOpen, onOpen: onConfirmOpen, onClose: onConfirmClose } = useDisclosure();
  const [form, setForm] = useState<GuardCompanyPayload>({ name: "", contact_email: null, contact_phone: null, address: null, manager_id: null, is_active: true });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [activeItem, setActiveItem] = useState<GuardCompanyRow | null>(null);

  const handleCreate = useCallback(() => { setEditingId(null); setForm({ name: "", contact_email: null, contact_phone: null, address: null, manager_id: null, is_active: true }); onOpen(); }, [onOpen]);
  const handleEdit = useCallback((row: GuardCompanyRow) => {
    setEditingId(row.id);
    setForm({ name: row.name, contact_email: row.contact_email ?? null, contact_phone: row.contact_phone ?? null, address: row.address ?? null, manager_id: row.manager_id ?? null, is_active: row.is_active });
    onOpen();
  }, [onOpen]);
  const handleSubmit = useCallback(async () => {
    if (!form.name.trim()) { showToast({ toast, message: "Nom obligatoire.", status: toastTypes.error }); return; }
    setSubmitting(true);
    try {
      if (editingId) { await updateGuardCompany(token, editingId, form); showToast({ toast, message: "Société mise à jour.", status: toastTypes.success }); }
      else { await createGuardCompany(token, form); showToast({ toast, message: "Société créée.", status: toastTypes.success }); }
      refetch(); onClose();
    } catch (e: any) { showToast({ toast, message: e?.response?.data?.detail || "Erreur.", status: toastTypes.error }); } finally { setSubmitting(false); }
  }, [token, form, editingId, refetch, onClose, toast]);
  const handleDeleteClick = useCallback((row: GuardCompanyRow) => { setActiveItem(row); onConfirmOpen(); }, [onConfirmOpen]);
  const handleDeleteConfirm = useCallback(async () => {
    if (!activeItem) return;
    setDeletingId(activeItem.id);
    try {
      await deleteGuardCompany(token, activeItem.id);
      showToast({ toast, message: "Société supprimée.", status: toastTypes.success });
      refetch();
      onConfirmClose();
    } catch (e: any) { showToast({ toast, message: e?.response?.data?.detail || "Erreur.", status: toastTypes.error }); } finally { setDeletingId(null); setActiveItem(null); }
  }, [token, activeItem, refetch, onConfirmClose, toast]);

  const col = createColumnHelper<GuardCompanyRow>();
  const columns = useMemo(() => [
    col.accessor("name", { header: "Nom", cell: (c) => <Text>{c.getValue()}</Text> }),
    col.accessor("contact_email", { header: "Email", cell: (c) => <Text>{c.getValue() ?? "—"}</Text> }),
    col.accessor("is_active", { header: "Actif", cell: (c) => <Text>{c.getValue() ? "Oui" : "Non"}</Text> }),
    col.display({ id: "action", header: "Action", cell: ({ row }) => <><Button size="sm" onClick={() => handleEdit(row.original)} mr={2}>Modifier</Button><Button size="sm" colorScheme="red" onClick={() => handleDeleteClick(row.original)}>Supprimer</Button></> }),
  ], [handleEdit, handleDeleteClick]);

  return (
    <>
      <Flex gap="18px" bg="very_light_gray" flex={1} flexDirection="column" py="20px">
        <WelcomeSection />
        <Flex flexDirection="column" px={{ base: "20px", sm: "24px" }}>
          <Flex justifyContent="space-between" alignItems="center" mb={4}>
            <Heading size="text2xl" as="h5" fontFamily="Arimo">Sociétés de gardiennage</Heading>
            <Button colorScheme="blue" onClick={handleCreate}>Ajouter</Button>
          </Flex>
          {error && <Box p={4} bg="red.50" borderRadius="md"><Text color="red.700">Erreur chargement.</Text></Box>}
          <Box bg="white" borderRadius="10px">
            {isLoading ? <Flex justify="center" py={10}><Spinner /></Flex> : <ReactTable columns={columns as any} data={list} size="sm" />}
            {!isLoading && !list.length && <Box p={8} textAlign="center"><Text color="gray.600">Aucune donnée.</Text></Box>}
          </Box>
        </Flex>
      </Flex>
      <Modal isOpen={isOpen} onClose={onClose}><ModalOverlay /><ModalContent>
        <ModalHeader>{editingId ? "Modifier" : "Ajouter"} une société</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={3}>
            <FormControl><FormLabel>Nom</FormLabel><Input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} /></FormControl>
            <FormControl><FormLabel>Email</FormLabel><Input type="email" value={form.contact_email ?? ""} onChange={(e) => setForm((p) => ({ ...p, contact_email: e.target.value || null }))} /></FormControl>
            <FormControl><FormLabel>Téléphone</FormLabel><Input value={form.contact_phone ?? ""} onChange={(e) => setForm((p) => ({ ...p, contact_phone: e.target.value || null }))} /></FormControl>
            <FormControl display="flex" alignItems="center"><FormLabel mb={0}>Actif</FormLabel><Switch isChecked={form.is_active} onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))} /></FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter><Button variant="ghost" mr={3} onClick={onClose}>Annuler</Button><Button colorScheme="blue" onClick={handleSubmit} isLoading={submitting}>Enregistrer</Button></ModalFooter>
      </ModalContent></Modal>
      <AlertDialog isOpen={isConfirmOpen} leastDestructiveRef={cancelRef} onClose={onConfirmClose}>
        <AlertDialogOverlay /><AlertDialogContent>
          <AlertDialogHeader>Supprimer cette société ?</AlertDialogHeader>
          <AlertDialogBody>Cette action est irréversible.</AlertDialogBody>
          <AlertDialogFooter><Button ref={cancelRef} onClick={onConfirmClose}>Annuler</Button><Button colorScheme="red" onClick={handleDeleteConfirm} isLoading={!!deletingId} ml={3}>Supprimer</Button></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// --- Guard Posts (need site_id: select from sites) ---
export function TrackingGuardPostsPage() {
  const toast = useToast();
  const cancelRef = useRef<any>(null);
  const { user } = useAuthentication();
  const token = user?.auth?.access_token;
  const sites = useSites();
  const { data: list, isLoading, error, refetch } = useTrackingAdminList("guard-posts", fetchGuardPosts);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isConfirmOpen, onOpen: onConfirmOpen, onClose: onConfirmClose } = useDisclosure();
  const [form, setForm] = useState<GuardPostPayload & { site_id: string }>({ site_id: "", name: "", qr_code: null, order: 0 });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [activeItem, setActiveItem] = useState<GuardPostRow | null>(null);

  const handleCreate = useCallback(() => { setEditingId(null); setForm({ site_id: "", name: "", qr_code: null, order: 0 }); onOpen(); }, [onOpen]);
  const handleEdit = useCallback((row: GuardPostRow) => {
    setEditingId(row.id);
    setForm({ site_id: row.site_id, name: row.name, qr_code: row.qr_code ?? null, order: row.order });
    onOpen();
  }, [onOpen]);
  const handleSubmit = useCallback(async () => {
    if (!form.site_id || !form.name.trim()) { showToast({ toast, message: "Site et nom obligatoires.", status: toastTypes.error }); return; }
    setSubmitting(true);
    try {
      const payload: GuardPostPayload = { site_id: form.site_id, name: form.name.trim(), qr_code: form.qr_code ?? undefined, order: form.order };
      if (editingId) { await updateGuardPost(token, editingId, payload); showToast({ toast, message: "Poste mis à jour.", status: toastTypes.success }); }
      else { await createGuardPost(token, payload); showToast({ toast, message: "Poste créé.", status: toastTypes.success }); }
      refetch(); onClose();
    } catch (e: any) { showToast({ toast, message: e?.response?.data?.detail || "Erreur.", status: toastTypes.error }); } finally { setSubmitting(false); }
  }, [token, form, editingId, refetch, onClose, toast]);
  const handleDeleteClick = useCallback((row: GuardPostRow) => { setActiveItem(row); onConfirmOpen(); }, [onConfirmOpen]);
  const handleDeleteConfirm = useCallback(async () => {
    if (!activeItem) return;
    setDeletingId(activeItem.id);
    try {
      await deleteGuardPost(token, activeItem.id);
      showToast({ toast, message: "Poste supprimé.", status: toastTypes.success });
      refetch();
      onConfirmClose();
    } catch (e: any) { showToast({ toast, message: e?.response?.data?.detail || "Erreur.", status: toastTypes.error }); } finally { setDeletingId(null); setActiveItem(null); }
  }, [token, activeItem, refetch, onConfirmClose, toast]);

  const siteMap = useMemo(() => { const m: Record<string, string> = {}; sites.forEach((s) => { m[String(s.id)] = s.name; }); return m; }, [sites]);
  const col = createColumnHelper<GuardPostRow>();
  const columns = useMemo(() => [
    col.accessor("name", { header: "Nom", cell: (c) => <Text>{c.getValue()}</Text> }),
    col.accessor("site_id", { header: "Site", cell: (c) => <Text>{siteMap[c.getValue()] ?? c.getValue()}</Text> }),
    col.accessor("order", { header: "Ordre", cell: (c) => <Text>{c.getValue()}</Text> }),
    col.display({ id: "action", header: "Action", cell: ({ row }) => <><Button size="sm" onClick={() => handleEdit(row.original)} mr={2}>Modifier</Button><Button size="sm" colorScheme="red" onClick={() => handleDeleteClick(row.original)}>Supprimer</Button></> }),
  ], [handleEdit, handleDeleteClick, siteMap]);

  return (
    <>
      <Flex gap="18px" bg="very_light_gray" flex={1} flexDirection="column" py="20px">
        <WelcomeSection />
        <Flex flexDirection="column" px={{ base: "20px", sm: "24px" }}>
          <Flex justifyContent="space-between" alignItems="center" mb={4}>
            <Heading size="text2xl" as="h5" fontFamily="Arimo">Points de contrôle (postes)</Heading>
            <Button colorScheme="blue" onClick={handleCreate}>Ajouter</Button>
          </Flex>
          {error && <Box p={4} bg="red.50" borderRadius="md"><Text color="red.700">Erreur chargement.</Text></Box>}
          <Box bg="white" borderRadius="10px">
            {isLoading ? <Flex justify="center" py={10}><Spinner /></Flex> : <ReactTable columns={columns as any} data={list} size="sm" />}
            {!isLoading && !list.length && <Box p={8} textAlign="center"><Text color="gray.600">Aucune donnée.</Text></Box>}
          </Box>
        </Flex>
      </Flex>
      <Modal isOpen={isOpen} onClose={onClose}><ModalOverlay /><ModalContent>
        <ModalHeader>{editingId ? "Modifier" : "Ajouter"} un poste</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={3}>
            <FormControl><FormLabel>Site</FormLabel>
              <select style={{ width: "100%", padding: "8px" }} value={form.site_id} onChange={(e) => setForm((p) => ({ ...p, site_id: e.target.value }))}>
                <option value="">— Choisir —</option>
                {sites.map((s) => <option key={s.id} value={String(s.id)}>{s.name}</option>)}
              </select>
            </FormControl>
            <FormControl><FormLabel>Nom</FormLabel><Input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} /></FormControl>
            <FormControl><FormLabel>Ordre</FormLabel><Input type="number" value={form.order} onChange={(e) => setForm((p) => ({ ...p, order: parseInt(e.target.value, 10) || 0 }))} /></FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter><Button variant="ghost" mr={3} onClick={onClose}>Annuler</Button><Button colorScheme="blue" onClick={handleSubmit} isLoading={submitting}>Enregistrer</Button></ModalFooter>
      </ModalContent></Modal>
      <AlertDialog isOpen={isConfirmOpen} leastDestructiveRef={cancelRef} onClose={onConfirmClose}>
        <AlertDialogOverlay /><AlertDialogContent>
          <AlertDialogHeader>Supprimer ce poste ?</AlertDialogHeader>
          <AlertDialogBody>Cette action est irréversible.</AlertDialogBody>
          <AlertDialogFooter><Button ref={cancelRef} onClick={onConfirmClose}>Annuler</Button><Button colorScheme="red" onClick={handleDeleteConfirm} isLoading={!!deletingId} ml={3}>Supprimer</Button></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// --- Incident Types ---
export function TrackingIncidentTypesPage() {
  const toast = useToast();
  const cancelRef = useRef<any>(null);
  const { user } = useAuthentication();
  const token = user?.auth?.access_token;
  const { data: list, isLoading, error, refetch } = useTrackingAdminList("incident-types", fetchIncidentTypes);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isConfirmOpen, onOpen: onConfirmOpen, onClose: onConfirmClose } = useDisclosure();
  const [form, setForm] = useState<IncidentTypePayload>({ name: "", code: "", is_active: true });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [activeItem, setActiveItem] = useState<IncidentTypeRow | null>(null);

  const handleCreate = useCallback(() => { setEditingId(null); setForm({ name: "", code: "", is_active: true }); onOpen(); }, [onOpen]);
  const handleEdit = useCallback((row: IncidentTypeRow) => {
    setEditingId(row.id);
    setForm({ name: row.name, code: row.code, is_active: row.is_active });
    onOpen();
  }, [onOpen]);
  const handleSubmit = useCallback(async () => {
    if (!form.name.trim() || !form.code.trim()) { showToast({ toast, message: "Nom et code obligatoires.", status: toastTypes.error }); return; }
    setSubmitting(true);
    try {
      if (editingId) { await updateIncidentType(token, editingId, form); showToast({ toast, message: "Type mis à jour.", status: toastTypes.success }); }
      else { await createIncidentType(token, form); showToast({ toast, message: "Type créé.", status: toastTypes.success }); }
      refetch(); onClose();
    } catch (e: any) { showToast({ toast, message: e?.response?.data?.detail || "Erreur.", status: toastTypes.error }); } finally { setSubmitting(false); }
  }, [token, form, editingId, refetch, onClose, toast]);
  const handleDeleteClick = useCallback((row: IncidentTypeRow) => { setActiveItem(row); onConfirmOpen(); }, [onConfirmOpen]);
  const handleDeleteConfirm = useCallback(async () => {
    if (!activeItem) return;
    setDeletingId(activeItem.id);
    try {
      await deleteIncidentType(token, activeItem.id);
      showToast({ toast, message: "Type supprimé.", status: toastTypes.success });
      refetch();
      onConfirmClose();
    } catch (e: any) { showToast({ toast, message: e?.response?.data?.detail || "Erreur.", status: toastTypes.error }); } finally { setDeletingId(null); setActiveItem(null); }
  }, [token, activeItem, refetch, onConfirmClose, toast]);

  const col = createColumnHelper<IncidentTypeRow>();
  const columns = useMemo(() => [
    col.accessor("code", { header: "Code", cell: (c) => <Text>{c.getValue()}</Text> }),
    col.accessor("name", { header: "Nom", cell: (c) => <Text>{c.getValue()}</Text> }),
    col.accessor("is_active", { header: "Actif", cell: (c) => <Text>{c.getValue() ? "Oui" : "Non"}</Text> }),
    col.display({ id: "action", header: "Action", cell: ({ row }) => <><Button size="sm" onClick={() => handleEdit(row.original)} mr={2}>Modifier</Button><Button size="sm" colorScheme="red" onClick={() => handleDeleteClick(row.original)}>Supprimer</Button></> }),
  ], [handleEdit, handleDeleteClick]);

  return (
    <>
      <Flex gap="18px" bg="very_light_gray" flex={1} flexDirection="column" py="20px">
        <WelcomeSection />
        <Flex flexDirection="column" px={{ base: "20px", sm: "24px" }}>
          <Flex justifyContent="space-between" alignItems="center" mb={4}>
            <Heading size="text2xl" as="h5" fontFamily="Arimo">Types d&apos;incidents</Heading>
            <Button colorScheme="blue" onClick={handleCreate}>Ajouter</Button>
          </Flex>
          {error && <Box p={4} bg="red.50" borderRadius="md"><Text color="red.700">Erreur chargement.</Text></Box>}
          <Box bg="white" borderRadius="10px">
            {isLoading ? <Flex justify="center" py={10}><Spinner /></Flex> : <ReactTable columns={columns as any} data={list} size="sm" />}
            {!isLoading && !list.length && <Box p={8} textAlign="center"><Text color="gray.600">Aucune donnée.</Text></Box>}
          </Box>
        </Flex>
      </Flex>
      <Modal isOpen={isOpen} onClose={onClose}><ModalOverlay /><ModalContent>
        <ModalHeader>{editingId ? "Modifier" : "Ajouter"} un type d&apos;incident</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={3}>
            <FormControl><FormLabel>Code</FormLabel><Input value={form.code} onChange={(e) => setForm((p) => ({ ...p, code: e.target.value }))} /></FormControl>
            <FormControl><FormLabel>Nom</FormLabel><Input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} /></FormControl>
            <FormControl display="flex" alignItems="center"><FormLabel mb={0}>Actif</FormLabel><Switch isChecked={form.is_active} onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))} /></FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter><Button variant="ghost" mr={3} onClick={onClose}>Annuler</Button><Button colorScheme="blue" onClick={handleSubmit} isLoading={submitting}>Enregistrer</Button></ModalFooter>
      </ModalContent></Modal>
      <AlertDialog isOpen={isConfirmOpen} leastDestructiveRef={cancelRef} onClose={onConfirmClose}>
        <AlertDialogOverlay /><AlertDialogContent>
          <AlertDialogHeader>Supprimer ce type ?</AlertDialogHeader>
          <AlertDialogBody>Cette action est irréversible.</AlertDialogBody>
          <AlertDialogFooter><Button ref={cancelRef} onClick={onConfirmClose}>Annuler</Button><Button colorScheme="red" onClick={handleDeleteConfirm} isLoading={!!deletingId} ml={3}>Supprimer</Button></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
